import { useState, useEffect } from 'react';
import { Phone, Menu, X } from 'lucide-react';

const navLinks = [
  { href: '#benefits', label: 'Fördelar' },
  { href: '#why-us', label: 'Varför oss' },
  { href: '#process', label: 'Arbetsgång' },
  { href: '#faq', label: 'Vanliga frågor' },
  { href: '#contact', label: 'Kontakt' },
];

export const Header = ({ onCtaClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 bg-[#2C3E50] flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">F</span>
            </div>
            <span className="font-heading font-semibold text-[#1F2937] text-lg hidden sm:block">
              Fönstermålning
            </span>
          </a>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[#1F2937] hover:text-[#C0392B] font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA and phone */}
          <div className="flex items-center gap-4">
            <a
              href="tel:08-254604"
              className="hidden md:flex items-center gap-2 text-[#2C3E50] font-medium hover:text-[#C0392B] transition-colors duration-200"
              data-testid="header-phone-link"
            >
              <Phone className="w-4 h-4" />
              08-25 46 04
            </a>
            <button
              onClick={onCtaClick}
              className="hidden sm:block btn-cta py-2.5 px-5 text-sm"
              data-testid="header-cta-button"
            >
              Få offert
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#2C3E50]"
              aria-label={isMobileMenuOpen ? 'Stäng meny' : 'Öppna meny'}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden absolute top-20 left-0 right-0 bg-white border-t border-[#E2E8F0] shadow-lg animate-fade-in"
            data-testid="mobile-menu"
          >
            <nav className="container-custom py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-[#1F2937] hover:text-[#C0392B] font-medium py-2 transition-colors duration-200"
                  data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-4 border-t border-[#E2E8F0]">
                <a
                  href="tel:08-254604"
                  className="flex items-center gap-2 text-[#2C3E50] font-semibold py-2"
                >
                  <Phone className="w-5 h-5" />
                  Ring oss: 08-25 46 04
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
